// MIDAS V3 — Chat message list (Phase 2 + Phase 6)
//
// Scroll container — max-width 820, centered. Renders chronological mix of:
//   • <ChatUserMessageV3> for `role === "user"`
//   • <ChatBotMessageV3> for `role === "assistant"`
//   • Day separators between days (v3 re-skinned eyebrow, NOT the v2 MUI
//     ChatDaySeparator)
//
// Auto-scroll-to-bottom on append (mirrors v2 behaviour).
//
// Streaming state: when the chatBot slice reports an in-flight stream for
// the active thread, we append a <StreamingStateV3 /> chrome at the bottom
// of the list (after the last persisted message). Phase 6 replaces the
// Phase 2 minimal placeholder with the full chrome — animated logo + halo +
// step chip + shimmer + elapsed timer.
//
// We feed StreamingStateV3 the buffer's `currentStep` directly so when the
// bot starts emitting step keys it lights up without any further frontend
// change.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChatMessage, POPUP_THREAD_ID } from "../../../../shared/chatBot/models/chatBotModels";
import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import ChatBotMessageV3 from "./ChatBotMessageV3";
import ChatUserMessageV3 from "./ChatUserMessageV3";
import ChatWelcomeV3 from "./ChatWelcomeV3";
import StreamingMarkdownV3 from "./StreamingMarkdownV3";
import StreamingStateV3 from "./StreamingStateV3";
import StepRowV3 from "./StepRowV3";
import ThinkingPanelV3 from "./ThinkingPanelV3";
import { isCcDevSurface } from "./ccSurface";

// ═══════════════════════════════════════════════════════════════
//  Helpers — verbatim from V2 logic
// ═══════════════════════════════════════════════════════════════

const isDifferentDay = (a: string, b: string): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() !== db.getFullYear() ||
    da.getMonth() !== db.getMonth() ||
    da.getDate() !== db.getDate()
  );
};

const isNewGroup = (a: ChatMessage, b: ChatMessage): boolean => {
  const diff = Math.abs(
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  return diff > 5 * 60 * 1000;
};

// ═══════════════════════════════════════════════════════════════
//  Day separator — v3-themed eyebrow
// ═══════════════════════════════════════════════════════════════

interface DaySeparatorProps {
  date: string;
}

const DaySeparatorV3: React.FC<DaySeparatorProps> = ({ date }) => {
  const { translate } = useTranslation("chatBot");

  const label = useMemo(() => {
    const msgDate = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (sameDay(msgDate, today)) {
      return translate("today", { defaultValue: "Today" }) as string;
    }
    if (sameDay(msgDate, yesterday)) {
      return translate("yesterday", { defaultValue: "Yesterday" }) as string;
    }
    return msgDate.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [date, translate]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "12px 0 14px 0",
        gap: 10,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: 1,
          height: 1,
          background: "var(--border-soft)",
          maxWidth: 120,
        }}
      />
      <span
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.1em",
          color: "var(--faint)",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        — {label} —
      </span>
      <span
        aria-hidden="true"
        style={{
          flex: 1,
          height: 1,
          background: "var(--border-soft)",
          maxWidth: 120,
        }}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  ChatMessageListV3
// ═══════════════════════════════════════════════════════════════

export interface ChatMessageListV3Props {
  /** Thread id for buffer-scoped streaming reads. Mirrors v2 MIDAS-SCOPESTREAM. */
  threadId?: string;
  /** MODEL-PICKER P3 (attach columns): render THESE messages instead of the
   *  global (primary) state.chatBot.messages. The attached columns keep their
   *  own per-thread arrays in midasAttachSlice — the SAME list component
   *  renders both surfaces so a column looks exactly like the primary chat. */
  messagesOverride?: ChatMessage[];
  /** Tighter gutters for narrow attached columns (design: 20px vs 26/32). */
  compact?: boolean;
  /** Attached columns never show the platform welcome screen — an empty
   *  attached thread just shows an empty scroll area. */
  suppressWelcome?: boolean;
}

const ChatMessageListV3: React.FC<ChatMessageListV3Props> = ({
  threadId,
  messagesOverride,
  compact = false,
  suppressWelcome = false,
}) => {
  const globalMessages = useAppSelector((state) => state.chatBot.messages);
  const messages = messagesOverride ?? globalMessages;

  // Thread-buffer scoped streaming state (verbatim selector pattern from v2).
  const effectiveThreadId = threadId || POPUP_THREAD_ID;
  const threadBuffer = useAppSelector(
    (state) => state.chatBot.threadBuffers[effectiveThreadId]
  );
  const globalIsLoading = useAppSelector((state) => state.chatBot.isLoading);
  const globalStreamingText = useAppSelector(
    (state) => state.chatBot.streamingText
  );
  const globalError = useAppSelector((state) => state.chatBot.error);
  const isLoading = threadId
    ? threadBuffer?.isLoading || false
    : globalIsLoading;
  // MODEL-PICKER-OSS: stream-failure surface. An error-only SSE stream (e.g.
  // the parked RunPod OSS pod emitting ONE sanitized `event: error` and no
  // answer) lands in threadBuffers[threadId].error via sendMessage.rejected —
  // isLoading resets, the composer re-enables, but V3 previously never READ
  // this field (V1 ChatMessageList.tsx:85 and V2 ChatMessageListV2.tsx:254
  // both do), so the turn failed silently. Mirror the V2 selector semantics:
  // thread-scoped buffer error when a threadId is supplied (primary chat AND
  // attach columns), global error otherwise. The buffer error clears on the
  // next send (registerStreamBuffer creates a fresh buffer) — same lifecycle
  // as V2.
  const error = threadId ? threadBuffer?.error || null : globalError;
  // Step key (e.g. "analyzing") from the buffer. Will be null today —
  // Q-MIDAS-V3-4 tracks the bot-side emit. StreamingStateV3 falls back to
  // elapsed-time buckets when this is null/undefined.
  const currentStep = threadBuffer?.currentStep ?? null;
  // Progressive token stream — populated by chatBotSlice.appendStreamingToken
  // per SSE chunk. V3 previously did NOT subscribe to this (the bug), so
  // users only saw the chrome until the message finalised. Mirrors the V2
  // selector pattern at ChatMessageListV2.tsx:247-253: thread-buffer scoped
  // when a threadId is supplied, falls back to the global field otherwise.
  const streamingText = threadId
    ? threadBuffer?.streamingText || ""
    : globalStreamingText || "";
  // MODEL-PICKER-11 (§14.2): live thinking transcript for the in-flight turn
  // (thinking picks only; per-thread buffer → works in attach columns for
  // free). `.cc`-gated with the picker — off-`.cc` the bot never emits the
  // frame anyway, this is belt+braces render gating.
  const thinkingText =
    isCcDevSurface() && threadId ? threadBuffer?.thinkingText || "" : "";
  // V3-SEGMENT-STREAMING (SP-2): the transient loop_progress receipt row for
  // the in-flight round (thread-scoped; popup has none). Rendered as a
  // StepRowV3 (`live` variant while unsettled) appended AFTER the streamed
  // blocks — or as the OPENER when a round dispatched tools with no preamble
  // (the marker line later supersedes it via the slice dedupe). Cleared on
  // stream completion, so the final message shows only marker-derived rows.
  const liveRow = threadId ? threadBuffer?.liveRow ?? null : null;
  const liveRowLabel = liveRow
    ? liveRow.settled
      ? liveRow.settledLabel || liveRow.labels.join(" · ")
      : liveRow.labels.join(" · ")
    : "";

  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const prevMessageCount = useRef(messages.length);

  // Track streaming start so the streaming chrome can show elapsed seconds.
  // We anchor at the first frame the buffer reports isLoading=true and reset
  // when the stream ends. This survives child re-renders.
  const streamingStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (isLoading) {
      if (streamingStartedAtRef.current === null) {
        streamingStartedAtRef.current = Date.now();
      }
    } else {
      streamingStartedAtRef.current = null;
    }
  }, [isLoading]);
  const startedAt = streamingStartedAtRef.current ?? Date.now();

  const handleScroll = () => {
    if (!listRef.current) return;
    const el = listRef.current;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setUserScrolledUp(distFromBottom > 100);
  };

  // Auto-scroll on new message append (unless the user has scrolled up).
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      if (!userScrolledUp) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages.length, userScrolledUp]);

  // Auto-scroll while streaming (the bot output grows; the user expects to
  // follow it). Don't fight the user if they scrolled up. `streamingText`
  // is in the dep array so the viewport pins to the bottom as prose grows
  // — mirrors V2 at ChatMessageListV2.tsx:327-331.
  useEffect(() => {
    if (isLoading && !userScrolledUp) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    // thinkingText in deps (§14.2): the thinking panel grows before any
    // answer token arrives — keep the viewport pinned during that phase too.
  }, [isLoading, userScrolledUp, streamingText, thinkingText]);

  // Empty thread → welcome state (primary chat only; columns suppress it).
  if (messages.length === 0 && !isLoading && !suppressWelcome) {
    return <ChatWelcomeV3 />;
  }

  return (
    <div
      ref={listRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: compact ? "20px 20px" : "26px 32px",
        scrollBehavior: "smooth",
      }}
    >
      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
        }}
      >
        {messages.map((msg, index) => {
          const prev = index > 0 ? messages[index - 1] : null;
          const next = index < messages.length - 1 ? messages[index + 1] : null;

          const showDaySeparator =
            !prev || isDifferentDay(prev.timestamp, msg.timestamp);
          const isFirstInGroup =
            !prev || prev.role !== msg.role || isNewGroup(prev, msg);

          // For phase 2 we don't render "showTimestamp at end of group" for
          // user bubbles unless it's the very last in a thread — keeps the
          // chat clean. v2 uses isLastInGroup; we keep the same signal.
          const isLastInGroup =
            !next || next.role !== msg.role || isNewGroup(msg, next);

          return (
            <React.Fragment key={msg.id}>
              {showDaySeparator && <DaySeparatorV3 date={msg.timestamp} />}
              {msg.role === "assistant" ? (
                <ChatBotMessageV3 message={msg} showAvatar={isFirstInGroup} />
              ) : (
                <ChatUserMessageV3
                  message={msg}
                  showTimestamp={isLastInGroup}
                />
              )}
            </React.Fragment>
          );
        })}
        {/* Streaming handoff — mirrors V2 at ChatMessageListV2.tsx:399-421.
            Two mutually exclusive branches:
              • isLoading && !streamingText → chrome (animated hairline +
                step pill + elapsed timer). Shows ONLY before the first
                token, so the user gets an immediate "thinking" signal.
              • isLoading && streamingText  → progressive prose grows in
                place of the chrome. The timer and step pill disappear
                because time-to-first-token is the only metric that
                matters once tokens are arriving (TTFT-priority principle).
            Final-state ChatBotMessageV3 takes over when the thunk lands
            the persisted message and isLoading flips back to false. */}
        {/* MODEL-PICKER-11 (§14.2): thinking panel ABOVE the answer prose.
            Auto-expanded while ONLY thinking streams; the moment answer
            tokens start, autoExpanded flips false → the panel default-
            collapses (re-expandable). The completed turn re-renders it from
            message.thinkingText inside ChatBotMessageV3. Once thinking
            deltas flow they ARE the visible progress, so the timer chrome
            below yields to the panel (TTFT-priority handoff pattern). */}
        {isLoading && thinkingText && (
          <ThinkingPanelV3
            text={thinkingText}
            streaming={!streamingText}
            autoExpanded={!streamingText}
          />
        )}
        {/* SP-2: the pre-first-token chrome now ALSO yields to a loop_progress
            live row — if a round opened with tools before any prose, show the
            gray row instead of the generic streaming chrome. */}
        {isLoading && !streamingText && !thinkingText && !liveRow && (
          <StreamingStateV3
            startedAt={startedAt}
            currentStep={currentStep}
          />
        )}
        {isLoading && streamingText && (
          <StreamingMarkdownV3
            text={streamingText}
            startedAt={startedAt}
          />
        )}
        {/* SP-2: live/settled loop_progress receipt row — appended AFTER the
            streamed prose/marker blocks (wire order: it is the current
            in-progress round). Also covers the "turn opens with a gray row"
            case (renders even when streamingText is empty). */}
        {isLoading && liveRow && (
          <StepRowV3 label={liveRowLabel} live={!liveRow.settled} />
        )}
        {/* Stream-failure block (MODEL-PICKER-OSS) — mirrors V2 at
            ChatMessageListV2.tsx:423-428, re-skinned in V3 chrome. Renders
            the sanitized error from the thread buffer once the stream has
            terminated (never alongside the streaming chrome). Covers the
            error-only SSE stream: spinner stops, composer re-enables, and
            the failure is VISIBLE instead of silent. */}
        {error && !isLoading && (
          <div
            role="alert"
            style={{
              margin: "10px 0 4px",
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid var(--red)",
              background: "var(--bg-soft)",
              color: "var(--red)",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {error}
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

export default ChatMessageListV3;
